import type { StateCreator } from 'zustand'
import type {
  MessageGiftSent,
  MessageToastMiddle,
  MessageUserLevelUp,
} from '../../types/lives/messages'
import type {
  NotifyGift,
  NotifyLuckEarned,
  NotifyRocketGameExploded,
} from '../../types/lives/notifies'

export type PositionReturnLuck = 'top' | 'left' | 'right'

interface State {
  notifyGiftSentQuantityList: Record<string, MessageGiftSent>
  notifyGiftSentReturnList: MessageToastMiddle | null
  notifyUserLevelUpList: MessageUserLevelUp[]
  notifyGiftLuckList: {
    top: NotifyLuckEarned | null
    left: NotifyLuckEarned | null
    right: NotifyLuckEarned | null
  }
  notifyGiftListToastSend: NotifyGift[]
  notifyGiftListToastEarned: NotifyGift[]
  currentNotifyGiftListToast: NotifyGift | null
  notifyUserJoinedLive: number[]
  notifyRocketGame: NotifyRocketGameExploded[]
}

interface Actions {
  clearNotifyGift: () => void

  addNotifyGiftSentQuantity: (notifyInfo: MessageGiftSent) => void
  addNotifyGiftSentReturnList: (notifyInfo: MessageToastMiddle) => void
  addNotifyUserLevelUpList: (notifyInfo: MessageUserLevelUp) => void
  addNotifyGiftToastSend: (notifyInfo: NotifyGift) => void
  addNotifyGiftToastEarned: (notifyInfo: NotifyGift) => void
  addNotifyGiftLuck: (notifyInfo: NotifyLuckEarned) => void
  addNotifyUserJoinedLive: (id: number) => void
  addNotifyRocketGame: (rocketGameData: NotifyRocketGameExploded) => void

  removeNotifyGiftLuck: (
    notifyInfo: NotifyLuckEarned,
    pos: PositionReturnLuck,
  ) => void
  removeNotifyGiftSentQuantity: (id: string) => void
  removeNotifyGiftToastSend: (notifyInfo: NotifyGift) => void
  removeNotifyGiftToastEarned: (notifyInfo: NotifyGift) => void
  removeNotifyGiftSentReturn: () => void
  removeNotifyUserLevelUp: (notifyInfo: MessageUserLevelUp) => void
  removeNotifyUserJoinedLive: (id: number) => void
  removeNotifyRocketGame: (id: string) => void

  clearNotifyUserJoinedLive: () => void
  clearGiftSentReturnList: () => void
  clearUserLevelUpList: () => void
  clearGiftLuck: () => void
  clearGiftSentQuantityList: () => void
  clearNotifyGiftToastEarned: () => void
  clearNotifyRocketGame: () => void
}

export type NotifyLiveSlice = State & Actions

export const notifyLiveStore: StateCreator<
  NotifyLiveSlice,
  [],
  [],
  NotifyLiveSlice
> = (set) => ({
  notifyGiftListToastSend: [],
  notifyGiftListToastEarned: [],
  notifyGiftSentQuantityList: {},
  notifyGiftSentReturnList: null,
  notifyUserLevelUpList: [],
  notifyUserJoinedLive: [],
  notifyGiftLuckList: {
    top: null,
    left: null,
    right: null,
  },
  notifyRocketGame: [],
  currentNotifyGiftListToast: null,
  addNotifyRocketGame: (notifyRocketGame) => {
    set((prevState) => {
      const rocketsGames = prevState.notifyRocketGame

      const check = rocketsGames.find((rocketGame) => {
        return (
          rocketGame.rocketGameId === notifyRocketGame.rocketGameId &&
          rocketGame.rocketGameLiveId === notifyRocketGame.rocketGameLiveId &&
          rocketGame.typeRocketGameProgress ===
            notifyRocketGame.typeRocketGameProgress &&
          rocketGame.liveId === rocketGame.liveId
        )
      })

      if (check) {
        return {
          notifyRocketGame: rocketsGames,
        }
      }

      return {
        notifyRocketGame: prevState.notifyRocketGame.concat({
          ...notifyRocketGame,
          id: String(Date.now()),
        }),
      }
    })
  },
  addNotifyGiftToastSend: (notifyInfo) => {
    set((prevState) => {
      const newToast =
        notifyInfo?.message?.topGlobal ??
        notifyInfo?.message?.topLocal ??
        notifyInfo?.message?.topSameCountry

      const currentToast =
        prevState.currentNotifyGiftListToast?.message?.topGlobal ??
        prevState.currentNotifyGiftListToast?.message?.topLocal ??
        prevState.currentNotifyGiftListToast?.message?.topSameCountry

      if (
        newToast?.isLuck &&
        currentToast?.isLuck &&
        currentToast?.userSentId === newToast?.userSentId &&
        prevState.currentNotifyGiftListToast
      ) {
        return {
          currentNotifyGiftListToast: {
            ...prevState.currentNotifyGiftListToast,
            message: notifyInfo.message,
          },
        }
      }

      if (!prevState.currentNotifyGiftListToast) {
        return {
          currentNotifyGiftListToast: notifyInfo,
        }
      }

      return {
        notifyGiftListToastSend:
          prevState.notifyGiftListToastSend.concat(notifyInfo),
      }
    })
  },
  addNotifyGiftSentQuantity: (notifyInfo) => {
    set((prevState) => {
      const list = { ...prevState.notifyGiftSentQuantityList }

      const giftName = notifyInfo?.gift?.name
      const id = `${notifyInfo?.userSentId}-${notifyInfo?.userReceivedId}-${giftName}`

      if (list[id]) {
        list[id].quantity += notifyInfo.quantity
      } else {
        list[id] = notifyInfo
        list[id].id = id
      }

      return {
        notifyGiftSentQuantityList: list,
      }
    })
  },
  addNotifyGiftSentReturnList: (notifyInfo) => {
    set({
      notifyGiftSentReturnList: notifyInfo,
    })
  },
  addNotifyUserLevelUpList: (notifyInfo) => {
    set((prevState) => ({
      notifyUserLevelUpList: prevState.notifyUserLevelUpList.concat(notifyInfo),
    }))
  },
  addNotifyGiftToastEarned: (notifyInfo) => {
    set((prevState) => {
      const newToast =
        notifyInfo?.message?.topGlobal ??
        notifyInfo?.message?.topLocal ??
        notifyInfo?.message?.topSameCountry

      const currentToast =
        prevState.currentNotifyGiftListToast?.message?.topGlobal ??
        prevState.currentNotifyGiftListToast?.message?.topLocal ??
        prevState.currentNotifyGiftListToast?.message?.topSameCountry

      if (
        newToast?.isLuck &&
        currentToast?.isLuck &&
        currentToast?.userSentId === newToast?.userSentId &&
        prevState.currentNotifyGiftListToast
      ) {
        return {
          currentNotifyGiftListToast: {
            ...prevState.currentNotifyGiftListToast,
            message: notifyInfo.message,
          },
        }
      }

      if (!prevState.currentNotifyGiftListToast) {
        return {
          currentNotifyGiftListToast: notifyInfo,
        }
      }

      return {
        notifyGiftListToastEarned:
          prevState.notifyGiftListToastEarned.concat(notifyInfo),
      }
    })
  },
  addNotifyUserJoinedLive: (id) => {
    const removeDuplicate = (array: number[]): number[] => {
      return array?.filter((i, index) => {
        return index === array.indexOf(i)
      })
    }

    set((prevState) => ({
      notifyUserJoinedLive: removeDuplicate(
        prevState.notifyUserJoinedLive,
      )?.concat(id),
    }))
  },
  addNotifyGiftLuck: (notifyInfo) => {
    set((prevState) => {
      const list = prevState.notifyGiftLuckList

      let { top, left, right } = { ...list }

      if (!top || (top && left && right)) {
        top = notifyInfo
      } else if (top && !left) {
        left = notifyInfo
      } else if (top && left && !right) {
        right = notifyInfo
      }

      return {
        notifyGiftLuckList: {
          top,
          left,
          right,
        },
      }
    })
  },
  removeNotifyRocketGame: (id) => {
    set((prevState) => ({
      notifyRocketGame: prevState.notifyRocketGame?.filter((info) => {
        return info?.id !== id
      }),
    }))
  },
  removeNotifyGiftToastSend: () => {
    set((prevState) => {
      const list = [...prevState.notifyGiftListToastSend]

      if (list?.length) {
        const [newNotifyInfo, ...listWithoutNewNotifyInfo] =
          prevState.notifyGiftListToastSend

        return {
          currentNotifyGiftListToast: newNotifyInfo,
          notifyGiftListToastSend: listWithoutNewNotifyInfo,
        }
      }

      return {
        currentNotifyGiftListToast: null,
      }
    })
  },
  removeNotifyGiftToastEarned: () => {
    set((prevState) => {
      const list = [...prevState.notifyGiftListToastEarned]

      if (list?.length) {
        const [newNotifyInfo, ...listWithoutNewNotifyInfo] =
          prevState.notifyGiftListToastEarned

        return {
          currentNotifyGiftListToast: newNotifyInfo,
          notifyGiftListToastEarned: listWithoutNewNotifyInfo,
        }
      }

      return {
        currentNotifyGiftListToast: null,
      }
    })
  },
  removeNotifyGiftSentQuantity: (id) => {
    set((prevState) => {
      const newObj = { ...prevState?.notifyGiftSentQuantityList }
      delete newObj[id]
      return { notifyGiftSentQuantityList: newObj }
    })
  },
  removeNotifyGiftSentReturn: () => {
    set({
      notifyGiftSentReturnList: null,
    })
  },
  removeNotifyUserLevelUp: (notifyInfo) => {
    set((prevState) => ({
      notifyUserLevelUpList: prevState.notifyUserLevelUpList?.filter((info) => {
        return info?.id !== notifyInfo?.id
      }),
    }))
  },
  removeNotifyUserJoinedLive: (id) => {
    set((prevState) => ({
      notifyUserJoinedLive: prevState.notifyUserJoinedLive?.filter((userId) => {
        return userId !== id
      }),
    }))
  },
  removeNotifyGiftLuck: (_notifyInfo, pos) => {
    set((prevState) => {
      const obj = { ...prevState.notifyGiftLuckList }

      obj[pos] = null

      return {
        notifyGiftLuckList: obj,
      }
    })
  },
  clearNotifyRocketGame: () => {
    set({ notifyRocketGame: [] })
  },
  clearNotifyGift: () => {
    set({ notifyGiftListToastSend: [] })
  },
  clearUserLevelUpList: () => {
    set({ notifyUserLevelUpList: [] })
  },
  clearNotifyUserJoinedLive: () => {
    set({ notifyUserJoinedLive: [] })
  },
  clearGiftSentReturnList: () => {
    set({ notifyGiftSentReturnList: null })
  },
  clearGiftSentQuantityList: () => {
    set({ notifyGiftSentQuantityList: {} })
  },
  clearNotifyGiftToastEarned: () => {
    set({ notifyGiftListToastEarned: [] })
  },
  clearGiftLuck: () => {
    set({
      notifyGiftLuckList: {
        top: null,
        left: null,
        right: null,
      },
    })
  },
})
